<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<q-icon name="fas fa-sign-in-alt" class="q-mb-sm" /> {{ $t('welcome_banner') }}
		</div>

		<div class="row gutter-sm q-mb-lg">
			<div class="col-xs-12 col-sm-12">
				<q-input
					v-model="title"
					ref="title"
					:float-label="$t('title')"
					:error="$v.title.$error"
					@input="$v.title.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-12 ">
				<div class="row gutter-sm flex">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('desktop_banner') }}</p>

						<uploader
							content_type="images"
							@fileUploaded="imageDesktopUploaded"
							:multiple="false"
							:cropper="true"
							:aspect_ratio="1200 / 457"
							:aspect_ratio_string="'1200/457'"
						/>

						<p v-if="image_desktop" class="q-mt-md file-url" ref="image_desktop">
							<q-icon name="fas fa-check" color="positive" />
							<a :href="image_desktop" target="_blank">
								{{ $truncateLink(image_desktop) }}</a
							>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="far fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br_desktop"
										:float-label="$t(`${lang.name}`)"
										ref="language_br_desktop"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us_desktop"
										:float-label="$t(`${lang.name}`)"
										ref="language_us_desktop"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es_desktop"
										:float-label="$t(`${lang.name}`)"
										ref="language_es_desktop"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr_desktop"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr_desktop"
									/>
								</q-field>
							</template>
						</div>
					</div>
				</div>
			</div>
			<div class="col-xs-12 col-sm-12 ">
				<div class="row gutter-sm flex">
					<div class="col-xs-12 col-sm-6">
						<p class="caption">{{ $t('mobile_banner') }}</p>

						<uploader
							content_type="images"
							@fileUploaded="imageMobileUploaded"
							:multiple="false"
							:cropper="true"
							:aspect_ratio="500 / 485"
							:aspect_ratio_string="'500/485'"
						/>

						<p v-if="image_mobile" class="q-mt-md file-url" ref="image_mobile">
							<q-icon name="fas fa-check" color="positive" />
							<a :href="image_mobile" target="_blank">
								{{ $truncateLink(image_mobile) }}</a
							>
						</p>
					</div>
					<div class="col-xs-12 col-sm-6">
						<p class="caption">
							{{ $t('alt_image') }}
							<q-icon
								name="far fa-question-circle"
								size="1.5rem"
								color="grey"
								class="q-ml-sm q-pb-sm"
							>
								<q-tooltip
									class="custom-tooltip"
									anchor="top left"
									self="bottom right"
								>
									{{ $t('alt_image_information_helper') }}
								</q-tooltip>
							</q-icon>
						</p>
						<div class="flex justify-between">
							<template v-for="lang in languages" class="row flex">
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'pt-br'">
									<q-input
										v-model="language_br_mobile"
										:float-label="$t(`${lang.name}`)"
										ref="language_br_mobile"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'en-us'">
									<q-input
										v-model="language_us_mobile"
										:float-label="$t(`${lang.name}`)"
										ref="language_us_mobile"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'es-es'">
									<q-input
										v-model="language_es_mobile"
										:float-label="$t(`${lang.name}`)"
										ref="language_es_mobile"
									/>
								</q-field>
								<q-field :count="170" :key="lang.id" v-if="lang.code === 'fr-fr'">
									<q-input
										v-model="language_fr_mobile"
										:float-label="$t(`${lang.name}`)"
										ref="language_fr_mobile"
									/>
								</q-field>
							</template>
						</div>
					</div>

					<div class="col-xs-12 col-sm-12"></div>
				</div>

				<p class="caption">{{ $t('content') }}</p>
				<q-editor
					v-model="content"
					:toolbar="[
						['bold', 'italic', 'strike', 'underline'],
						['left', 'center', 'right', 'justify'],
						['link', 'upload'],
						[
							{
								label: null,
								icon: 'fas fa-font',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code'],
							},
							{
								label: null,
								icon: 'fas fa-text-height',
								fixedLabel: true,
								fixedIcon: true,
								list: 'no-icons',
								options: [
									'size-1',
									'size-2',
									'size-3',
									'size-4',
									'size-5',
									'size-6',
									'size-7',
								],
							},
							'removeFormat',
						],
						['quote', 'unordered', 'ordered'],
						['custom_btn'],
					]"
					:definitions="{
						bold: { label: null, icon: 'fas fa-bold' },
						italic: { label: null, icon: 'fas fa-italic' },
						strike: { label: null, icon: 'fas fa-strikethrough' },
						underline: { label: null, icon: 'fas fa-underline' },
						left: { label: null, icon: 'fas fa-align-left' },
						center: { label: null, icon: 'fas fa-align-center' },
						right: { label: null, icon: 'fas fa-align-right' },
						justify: { label: null, icon: 'fas fa-align-justify' },
						link: { label: null, icon: 'fas fa-link' },
						upload: { tip: null, icon: 'fas fa-file-image', handler: insertImg },
						removeFormat: { label: null, icon: 'fas fa-eraser' },
						quote: { label: null, icon: 'fas fa-quote-right' },
						unordered: { label: null, icon: 'fas fa-list-ul' },
						ordered: { label: null, icon: 'fas fa-list-ol' },
					}"
				>
					<q-btn
						slot="custom_btn"
						dense
						flat
						color="black"
						icon="fas fa-code"
						label="HTML"
						@click="show_html = true"
					/>
				</q-editor>
			</div>

			<div class="col-xs-12 col-sm-12">
				<q-select
					v-model="banner_welcome_type"
					:options="bannerTypes"
					:float-label="$t('banner_type')"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="start_date"
					ref="start_date"
					type="date"
					:float-label="$t('start_date')"
					format="DD/MM/YYYY"
					:error="$v.start_date.$error"
					@input="$v.start_date.$touch"
				/>
			</div>

			<div class="col-xs-12 col-sm-6">
				<q-datetime
					v-model="end_date"
					ref="end_date"
					type="date"
					:float-label="$t('end_date')"
					format="DD/MM/YYYY"
					:error="$v.end_date.$error"
					@input="$v.end_date.$touch"
				/>
			</div>
		</div>

		<q-tabs v-model="tab" color="secondary" class="shadow-1">
			<q-tab
				slot="title"
				name="permissions"
				icon="fas fa-clipboard-check"
				:label="$t('permissions')"
			/>

			<q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
				<div class="row gutter-sm">
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('areas') }}</p>
						<q-checkbox
							class="q-mb-sm"
							v-model="all_areas"
							:val="true"
							:label="$t('select_all_areas')"
							@input="checkAllAreas()"
						/>
						<select-multiple
							:filters="area"
							:options="areas"
							:error="$v.area.$error"
							:input="$v.area.$touch"
							@on_value="area = $event"
						/>
					</div>
					<div class="col-xs-12 col-sm-12">
						<p class="caption">{{ $t('profiles') }}</p>

						<div class="row gutter-sm">
							<div class="col-xs-12 col-sm-12">
								<q-checkbox
									v-model="all_profiles"
									:val="true"
									:label="$t('all')"
									@input="checkAll()"
								/>
							</div>

							<div
								class="col-xs-12 col-sm-4"
								ref="profile"
								v-for="(item, index) in profiles"
								:key="index"
								:class="getErrorClass($v.profile.$error)"
							>
								<q-checkbox
									keep-color
									v-model="profile"
									:val="item.value"
									:label="item.label"
									@input="$v.profile.$touch"
									:color="getErrorColor($v.profile.$error)"
								/>
							</div>

							<div class="col-xs-12 col-sm-12">
								<q-select
									v-model="user_group"
									ref="user_group"
									:options="user_groups"
									:float-label="$t('groups')"
									:error="$v.user_group.$error"
									@input="$v.user_group.$touch"
									multiple
									chips
								/>
							</div>
						</div>
					</div>
				</div>
			</q-tab-pane>
		</q-tabs>

		<div class="row gutter-sm q-my-sm">
			<div class="col-xs-12 col-sm-12">
				<p class="caption">{{ $t('status') }}</p>
				<q-btn-toggle v-model="status" :toggle-color="getColorStatus" :options="statuses" />
			</div>
		</div>

		<div class="row gutter-sm justify-end q-mt-lg">
			<div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
				<q-btn
					color="tertiary"
					class="full-width"
					:label="$t('back')"
					icon="fas fa-chevron-left"
					@click="$router.push({ name: 'banners_welcome' })"
				/>
			</div>

			<div class="col-xs-12 col-sm-3" :class="getOrder('save')">
				<q-btn
					color="secondary"
					class="full-width"
					:label="$t('save')"
					icon="far fa-save"
					@click="saveData()"
				/>
			</div>
		</div>

		<q-modal
			v-model="show_html"
			:content-css="{ minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '80vh' }"
			no-backdrop-dismiss
			no-esc-dismiss
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_html = false" icon="fas fa-times" />
					<q-toolbar-title>
						HTML
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div class="row">
						<div class="col-12">
							<q-input
								type="textarea"
								rows="10"
								float-label="HTML"
								v-model="content"
							/>
						</div>
					</div>

					<div class="row gutter-sm justify-end q-mt-lg">
						<div class="col-xs-12 col-sm-3">
							<q-btn
								color="tertiary"
								class="full-width"
								:label="$t('close')"
								icon="fas fa-ban"
								@click="show_html = false"
							/>
						</div>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { date } from 'quasar'
import Uploader from 'components/Uploader'
import * as moment from 'moment'
import { BlobServiceClient } from '@azure/storage-blob'
import SelectMultiple from 'components/utils/SelectMultiple'
export default {
	i18nOptions: { namespaces: ['common', 'welcome_banners', 'admin'] },

	components: {
		uploader: Uploader,
		'select-multiple': SelectMultiple,
	},

	data() {
		return {
			language_br_desktop: '',
			language_us_desktop: '',
			language_es_desktop: '',
			language_fr_desktop: '',
			language_br_mobile: '',
			language_us_mobile: '',
			language_es_mobile: '',
			language_fr_mobile: '',
			id_banner: '',
			title: '',
			content: '',
			start_date: null,
			end_date: null,
			status: 1,
			statuses: [
				{ label: this.$t('published'), value: 1 },
				{ label: this.$t('unpublished'), value: 2 },
			],
			area: [],
			profile: [],
			tab: 'permissions',
			user_group: [],
			all_profiles: false,
			show_html: false,
			image_desktop: '',
			image_mobile: '',
			banner_welcome_type: 1,

			// Q-editor Image to be uploaded
			upload_url: process.env.API_URL + '/files/policy/',
			urls: [],
			cancel_token: '',
			source: '',
			crop_modal: false,
			original_image: '',
			fileUrl: '',
			all_areas: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			user_groups: 'registration_lists/group_users',
			profiles: 'registration_lists/profiles',
			welcome_banner_types: 'registration_lists/welcome_banner_types',
			languages: 'params/languages',
		}),

		getHeader() {
			return {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			}
		},

		getColorStatus() {
			return this.status == 1 ? 'positive' : 'negative'
		},

		getMaxWidth() {
			return this.$q.platform.is.mobile ? '100vw' : '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},

		bannerTypes() {
			const types = []
			this.welcome_banner_types.forEach((type) => {
				types.push({
					label: this.$t(type.slug),
					value: type.value,
				})
			})

			return types
		},
	},

	methods: {
		saveData() {
			this.$v.$touch()

			if (!this.$v.$error) {
				this.$q.loading.show()

				const jsonData = {
					title: this.title,
					content: this.$clearEmoticons(this.content),
					image_desktop: this.image_desktop,
					image_mobile: this.image_mobile,
					banner_welcome_type: this.banner_welcome_type,
					start_date: date.formatDate(this.start_date, 'YYYY-MM-DD'),
					end_date: date.formatDate(this.end_date, 'YYYY-MM-DD'),
					status: this.status,
					areas: this.area,
					profiles: this.profile,
					user_groups: this.user_group,
				}

				jsonData.banner_welcome_image_alt_texts = [
					{
						alt_text: this.language_br_desktop || '',
						image_type: 1,
						language: 'pt-br',
					},
					{
						alt_text: this.language_us_desktop || '',
						image_type: 1,
						language: 'en-us',
					},
					{
						alt_text: this.language_es_desktop || '',
						image_type: 1,
						language: 'es-es',
					},
					{
						alt_text: this.language_fr_desktop || '',
						image_type: 1,
						language: 'fr-fr',
					},
					{
						alt_text: this.language_br_mobile || '',
						image_type: 2,
						language: 'pt-br',
					},
					{
						alt_text: this.language_us_mobile || '',
						image_type: 2,
						language: 'en-us',
					},
					{
						alt_text: this.language_es_mobile || '',
						image_type: 2,
						language: 'es-es',
					},
					{
						alt_text: this.language_fr_mobile || '',
						image_type: 2,
						language: 'fr-fr',
					},
				]

				const config = this.getHeader
				config.headers['Content-Type'] = 'application/json'

				if (this.id_banner !== 0) {
					this.$axios
						.put(`/admin_banners_welcome/banners/${this.id_banner}/`, jsonData, config)
						.then(() => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('welcome_banner_edit_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.$router.push({ name: 'banners_welcome' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							console.log(e)
							if (e.response.data.error) {
								this.$q.notify({
									message: this.$t(e.response.data.details),
									type: 'negative',
									icon: 'fas fa-exclamation-triangle',
									position: 'top',
								})
							}
						})
				} else {
					this.$axios
						.post(`/admin_banners_welcome/banners/`, jsonData, config)
						.then(() => {
							this.$q.loading.hide()

							this.$q.notify({
								message: this.$t('welcome_banner_add_success'),
								type: 'positive',
								icon: 'far fa-save',
							})

							this.$router.push({ name: 'banners_welcome' })
						})
						.catch((e) => {
							this.$q.loading.hide()
							if (e.response.data.error) {
								this.$q.notify({
									message: this.$t(e.response.data.details),
									type: 'negative',
									icon: 'fas fa-exclamation-triangle',
									position: 'top',
								})
							}
						})
				}
			} else {
				this.$formInputErrorFocus(this.$v, this.$refs)
				this.$q.notify({
					message: this.$t('make_sure_that_all_fields_are_filled_in'),
					type: 'negative',
					icon: 'fas fa-exclamation-triangle',
					position: 'top',
				})
			}
		},

		fetchData() {
			this.$q.loading.show()

			this.$axios
				.get(`/admin_banners_welcome/banners/${this.id_banner}/`, this.getHeader)
				.then((response) => {
					this.title = response.data.title
					this.content = response.data.content ? response.data.content : ''
					this.start_date = moment(response.data.start_date).format('MM/DD/YYYY')
					this.end_date = moment(response.data.end_date).format('MM/DD/YYYY')
					this.status = response.data.status
					this.profile = response.data.profiles
					this.area = response.data.areas
					this.user_group = response.data.user_groups
					this.image_desktop = response.data.image_original_desktop
					this.image_mobile = response.data.image_original_mobile
					this.banner_welcome_type = response.data.banner_welcome_type
					const altTexts = response.data.banner_welcome_image_alt_texts || []
					const altTextsMap = altTexts.reduce((result, altTextObj) => {
						const { alt_text, image_type, language } = altTextObj
						const altTextKey = `language_${language.replace(/-/g, '_')}_${
							image_type === 1 ? 'desktop' : 'mobile'
						}`
						result[altTextKey] = alt_text || ''
						return result
					}, {})

					this.language_br_desktop = altTextsMap.language_pt_br_desktop
						? altTextsMap.language_pt_br_desktop
						: ''

					this.language_br_mobile = altTextsMap.language_pt_br_mobile
						? altTextsMap.language_pt_br_mobile
						: ''

					this.language_es_desktop = altTextsMap.language_es_es_desktop
						? altTextsMap.language_es_es_desktop
						: ''

					this.language_es_mobile = altTextsMap.language_es_es_mobile
						? altTextsMap.language_es_es_mobile
						: ''

					this.language_fr_desktop = altTextsMap.language_fr_fr_desktop
						? altTextsMap.language_fr_fr_desktop
						: ''

					this.language_fr_mobile = altTextsMap.language_fr_fr_mobile
						? altTextsMap.language_fr_fr_mobile
						: ''

					this.language_us_desktop = altTextsMap.language_en_us_desktop
						? altTextsMap.language_en_us_desktop
						: ''

					this.language_us_mobile = altTextsMap.language_en_us_mobile
						? altTextsMap.language_pt_br_mobile
						: ''

					this.$q.loading.hide()
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		insertImg() {
			const input = document.createElement('input')
			input.type = 'file'
			input.accept = '.png, .jpg, .gif'
			let file

			input.onchange = (_) => {
				const files = Array.from(input.files)
				file = files[0]

				this.uploadFile(file)
			}
			input.click()
		},

		uploadFile(file) {
			let date = moment()

			let fd = new FormData()
			fd.append('filename', file.name)
			fd.append(
				'file_directory',
				`media/html_images/banners/${date.year()}/${date.month() + 1}/`
			)

			this.$axios
				.post('/api/files/policy/', fd, this.getHeader, { cancelToken: this.source.token })
				.then((response) => {
					return new Promise((resolve) => {
						this.$q.loading.show()

						if (response.data.origin === 'azure') {
							const blobServiceClient = new BlobServiceClient(
								`${response.data.base_url}?${response.data.sas_token}`
							)

							const main = async () => {
								const container_client = blobServiceClient.getContainerClient('')

								const block_blob_client = container_client.getBlockBlobClient(
									response.data.filename
								)
								const upload_blob_response = await block_blob_client.uploadBrowserData(
									file
								)

								let fd_complete = new FormData()
								fd_complete.append('uploaded', true)
								fd_complete.append('fileSize', file.size)
								fd_complete.append('file', response.data.file_id)

								if (this.custom_directory) {
									fd_complete.append('not_signed', true)
									fd_complete.append('bucket_name', 'static')
								}

								this.$axios
									.post('/api/files/complete/', fd_complete, this.getHeader)
									.then((response) => {
										this.fileUrl = response.data.fileUrl
										document.execCommand(
											'insertHTML',
											true,
											'<div><img class="uploadBase" src="' +
												this.fileUrl +
												'" style="max-width: 100%" /></div>'
										)
									})
									.finally(() => {
										resolve()
										this.$q.loading.hide()
									})
							}

							main()
						} else {
							let content_type =
								file.type != '' ? file.type : 'application/octet-stream'
							let policy = response.data
							let file_name = policy.filename
							let key_path = policy.file_bucket_path

							let fd_s3 = new FormData()
							fd_s3.append('key', key_path + file_name)
							fd_s3.append('acl', 'private')
							fd_s3.append('Content-Type', content_type)
							fd_s3.append('AWSAccessKeyId', policy.key)
							fd_s3.append('Policy', policy.policy)
							fd_s3.append('filename', file_name)
							fd_s3.append('Signature', policy.signature)
							fd_s3.append('file', file)

							this.cancel_token = this.$axios.CancelToken
							this.source = this.cancel_token.source()

							this.$axios
								.post(process.env.BUCKET_URL, fd_s3, {
									// onUploadProgress: function( event ) {
									//     updateProgress(event.loaded / event.total)
									// },
									// cancelToken: this.source.token
								})
								.then((response) => {
									let fd_complete = new FormData()
									fd_complete.append('uploaded', true)
									fd_complete.append('fileSize', file.size)
									fd_complete.append('file', policy.file_id)

									this.$axios
										.post('/api/files/complete/', fd_complete, this.getHeader)
										.then((response) => {
											let data = {
												file_url: response.data.fileUrl,
											}

											this.$axios
												.post('/utils/presigned_url/', data, this.getHeader)
												.then((response) => {
													document.execCommand(
														'insertHTML',
														true,
														`<div><img class="uploadBase" src="${
															response.data.file_url
														}" style="max-width: 100%" /></div>`
													)
												})
												.catch((e) => {
													console.log(e)
												})
												.finally(() => {
													this.$q.loading.hide()
												})
										})
										.finally(() => {
											this.$q.loading.hide()
										})
								})
								.finally(() => {
									resolve()
								})
						}
					})
				})
				.catch((e) => {
					console.log(e)
					this.$q.loading.hide()
				})
		},

		checkAll() {
			if (this.all_profiles) {
				this.profiles.forEach((element) => {
					this.profile.push(element.value)
				})
			} else {
				this.profile = []
			}
		},

		getErrorColor(state) {
			return state ? 'negative' : ''
		},

		getErrorClass(state) {
			return state ? 'checkbox-error' : ''
		},

		getOrder(name) {
			if (name == 'cancel') {
				return this.$q.platform.is.mobile ? 'order-last' : 'order-first'
			} else if (name == 'save') {
				return this.$q.platform.is.mobile ? 'order-first' : 'order-last'
			}
		},

		imageDesktopUploaded(url) {
			this.image_desktop = url
		},

		imageMobileUploaded(url) {
			this.image_mobile = url
		},

		checkAllAreas() {
			if (this.all_areas) {
				this.area = this.areas.map((e) => e.value)
			} else {
				this.area = []
			}
		},
	},

	created() {
		this.id_banner = this.$route.params.id
		if (this.id_banner != 0) this.fetchData()
	},

	validations() {
		return {
			title: { required },
			start_date: { required },
			end_date: { required },
			area: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			profile: {
				requiredIf: requiredIf(() => {
					if (this.profile.length == 0 && this.area.length == 0) {
						if (
							this.profile.length == 0 &&
							this.area.length == 0 &&
							this.user_group.length == 0
						)
							return true
						else return false
					} else {
						return true
					}
				}),
			},
			user_group: {
				requiredIf: requiredIf(() => {
					if (
						this.profile.length == 0 &&
						this.area.length == 0 &&
						this.user_group.length == 0
					) {
						return true
					} else {
						return false
					}
				}),
			},
		}
	},
}
</script>

<style></style>
